<template>
	<div
		class="pt-2 amelipro-textarea"
		:class="fieldClasses"
		:style="globalFieldStyles"
	>
		<div
			class="d-inline-flex align-start"
			:class="labelClasses"
		>
			<label
				class="mr-2 amelipro-textarea__label"
				:for="id"
				:style="labelStyles"
			>
				{{ label }}

				<span v-if="ariaRequired">
					<span aria-hidden="true">
						&nbsp;*
					</span>

					<span class="d-sr-only">
						&nbsp;Champ obligatoire
					</span>
				</span>
			</label>

			<slot name="labelInfo" />
		</div>

		<VTextarea
			:id="id"
			ref="inputTextArea"
			v-model="inputValue"
			:aria-describedby="describedby"
			:aria-invalid="!valid"
			:aria-required="ariaRequired"
			auto-grow
			:background-color="disabled ? 'ap-grey lighten-2' : 'ap-white'"
			class="amelipro-textarea-input"
			color="ap-blue darken-1"
			:counter="counter"
			:disabled="disabled"
			outlined
			:readonly="readonly"
			:rules="inputRules"
			:style="inputStyles"
			v-bind="$attrs"
			v-on="$listeners"
			@blur="focused = false"
			@change="emitChangeEvent"
			@focus="focused = true"
		>
			<template #append>
				<slot name="append" />
			</template>

			<template #append-outer>
				<slot name="append-outer" />
			</template>

			<template #message="{ message }">
				<AmeliproMessage
					:id="errorId"
					no-icon
					text
					type="error"
				>
					<p class="mb-0">
						{{ message }}
					</p>
				</AmeliproMessage>
			</template>
		</VTextarea>
	</div>
</template>

<script setup lang="ts">
	import { PropType, computed, onMounted, ref } from 'vue';
	import AmeliproMessage from '../AmeliproMessage/AmeliproMessage.vue';
	import { IndexedObject } from '../../types';
	import { InputTextArea } from './types';
	import { VTextarea } from 'vuetify/lib';
	import { ValidationRule } from '../../rules/types';
	import { convertToHex } from '../../functions/convertToHex';
	import { required } from '../../rules/required';

	const props = defineProps({
		ariaRequired: {
			type: Boolean,
			default: false,
		},
		counter: {
			type: Number,
			default: 255,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		globalMaxWidth: {
			type: String,
			default: undefined,
		},
		globalMinWidth: {
			type: String,
			default: undefined,
		},
		globalWidth: {
			type: String,
			default: undefined,
		},
		horizontal: {
			type: Boolean,
			default: false,
		},
		id: {
			type: String,
			required: true,
		},
		inputMaxWidth: {
			type: String,
			default: undefined,
		},
		inputMinWidth: {
			type: String,
			default: undefined,
		},
		label: {
			type: String,
			required: true,
		},
		labelMaxWidth: {
			type: String,
			default: undefined,
		},
		labelMinWidth: {
			type: String,
			default: undefined,
		},
		readonly: {
			type: Boolean,
			default: false,
		},
		rules: {
			type: Array as PropType<ValidationRule[]>,
			default: () => [],
		},
		value: {
			type: String,
			default: undefined,
		},
	});

	const emit = defineEmits(['change']);

	const inputTextArea = ref<InputTextArea>({ valid: true, hasMessages: false });
	const displayError = computed(() => !inputTextArea.value.valid && inputTextArea.value.hasMessages);
	const valid = ref(true);
	const focused = ref(false);

	//v-model
	const inputValue = computed({
		get: (): string => props.value,
		set: (newValue: string): void => {
			emit('change', newValue);
		},
	});

	onMounted(() => {
		props.rules.forEach(emitChangeEvent);
	});

	const inputRules = computed<ValidationRule[]>(() => {
		//const { rules } = props; => provoque des mutations indésirables de props.rules
		const rules = [
			...props.rules,
		];

		if (props.ariaRequired) {
			rules.push(required);
		}

		return rules;
	});

	const errorId = computed<string>(() => `${props.id}-error`);

	const fieldClasses = computed<string>(() => {
		let classes = 'd-flex flex-column';

		if (props.horizontal) {
			classes = 'd-flex flex-column flex-md-row';
		}

		return classes;
	});

	const labelClasses = computed<string>(() => {
		let classes = '';

		if (props.horizontal) {
			classes = 'mt-md-2 mr-md-2';
		}

		return classes;
	});

	const globalFieldStyles = computed<IndexedObject>(() => ({
		maxWidth: props.globalMaxWidth,
		minWidth: props.globalMinWidth,
		width: props.globalWidth,
	}));

	const inputStyles = computed<IndexedObject>(() => ({
		maxWidth: props.inputMaxWidth,
		minWidth: props.inputMinWidth,
	}));

	const labelStyles = computed<IndexedObject>(() => ({
		maxWidth: props.labelMaxWidth,
		minWidth: props.labelMinWidth,
	}));

	const describedby = computed<string | undefined>(() => (valid.value ? undefined : errorId.value));

	const emitChangeEvent = (): void => {
		emit('change', inputValue.value);
		valid.value = inputTextArea.value.valid;
	};

	const inputBorderStyle = computed<string>(() => {
		let borderColor = convertToHex('ap-grey darken-1');

		if (focused.value) {
			borderColor = convertToHex('ap-blue darken-1');
		}

		if (displayError.value) {
			borderColor = convertToHex('ap-red');
		}

		if (props.disabled) {
			borderColor = convertToHex('ap-grey lighten-2');
		}

		return `1px solid ${borderColor}`;
	});
</script>

<style lang="scss" scoped>
	.amelipro-textarea-input {
		:deep(.v-input__slot) {
			border: v-bind(inputBorderStyle);
			border-radius: $input-radius;

			fieldset {
				border: 0;
			}
		}
	}
	.amelipro-textarea__label {
		margin-bottom: 4px;
		font-weight: $label-font-weight;
	}
</style>
